import { TitleProps } from '@/shared/types/title';
import { cn } from '@/shared/utils/cn';
import { Icon } from '@/modules/Icon';

/**
 * Title - Componente de título con soporte para iconos
 *
 * Componente que renderiza un título (h1) con opción de mostrar un icono.
 * Soporta iconos duotone y centrado del contenido.
 *
 * @example
 * ```tsx
 * // Título simple
 * <Title title="Mi Título" />
 *
 * // Título con icono
 * <Title
 *   title="Configuración"
 *   icon="SettingsOutlined"
 * />
 *
 * // Título centrado
 * <Title title="Centrado" center />
 *
 * // Título con children
 * <Title>Mi Título</Title>
 * ```
 *
 * @see {@link TitleProps} para la lista completa de props
 */
export const Title = ({
  title,
  children,
  className,
  icon,
  iconDuotonePrimary,
  iconDuotoneSecondary,
  iconDuotoneOpacityPrimary,
  iconDuotoneOpacitySecondary,
  center,
}: TitleProps) => {
  // Usar title si está disponible, sino usar children
  const content = title || children;

  return (
    <div className={cn('ui-imj ui-flex ui-items-center ui-gap-x-8', center && 'ui-justify-center')}>
      {icon && (
        <Icon
          name={icon}
          background
          color="blue"
          colorDuotonePrimary={iconDuotonePrimary}
          colorDuotoneSecondary={iconDuotoneSecondary}
          opacityDuotonePrimary={iconDuotoneOpacityPrimary}
          opacityDuotoneSecondary={iconDuotoneOpacitySecondary}
        />
      )}
      <h1
        className={cn(
          'ui-text-heading-h3 ui-font-heading-h3 ui-leading-heading-h3 ui-tracking-heading-h3',
          className,
        )}
      >
        {content}
      </h1>
    </div>
  );
};
